import { Track } from "@/src/lib/tracking";
import { mergeAdjustAdidIntoHeaders } from "@/src/lib/bridge/adjustRequestHeaders";
import { PayChannel } from "@features/api/memberApi";
import { getMemberApi } from "@/src/services/helper";

type MemberApiLike = Awaited<ReturnType<typeof getMemberApi>>;

const CONFIRMED_TOKENS_KEY = "edgen_payment_confirmed_tokens_v1";
const MAX_CONFIRMED_TOKENS = 20;

/** 读取本进程内已成功 confirm 的 purchase_token 集合（用于幂等）。 */
function readConfirmedTokens(): Set<string> {
  if (typeof window === "undefined") {
    return new Set();
  }
  try {
    const raw = sessionStorage.getItem(CONFIRMED_TOKENS_KEY);
    if (!raw) {
      return new Set();
    }
    const parsed = JSON.parse(raw) as string[];
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
}

/** 记录已 confirm 的 token，避免 resume 重放时重复请求后端。 */
function rememberConfirmedToken(token: string): void {
  if (typeof window === "undefined" || !token) {
    return;
  }
  const set = readConfirmedTokens();
  set.add(token);
  const arr = Array.from(set).slice(-MAX_CONFIRMED_TOKENS);
  try {
    sessionStorage.setItem(CONFIRMED_TOKENS_KEY, JSON.stringify(arr));
  } catch {
    // ignore
  }
}

/**
 * 判断 purchase_token / transactionId 是否已在当前进程 confirm 过。
 * reconcile 重放与正常购买路径共用，保证幂等。
 */
function hasConfirmedPurchaseToken(token: string): boolean {
  return readConfirmedTokens().has(token);
}

/**
 * 最佳努力调用 `payment/confirm` 落库权益。
 * 已 confirm 的 token 会跳过；失败只打日志，不向外抛错，避免阻塞 UI 终态。
 */
export async function confirmPurchaseBestEffort(options: {
  payChannel: PayChannel.APPLE_IAP | PayChannel.GOOGLE_IAP;
  purchaseToken: string;
  backendProductId?: string;
  memberApi?: MemberApiLike | null;
  confirmMaxWaitMs?: number;
}): Promise<void> {
  const { payChannel, purchaseToken, backendProductId, memberApi } = options;
  if (!purchaseToken || hasConfirmedPurchaseToken(purchaseToken)) {
    return;
  }

  const api = memberApi ?? (await getMemberApi());
  if (!api?.payment?.paymentConfirm) {
    return;
  }

  try {
    await api.payment.paymentConfirm(
      {
        purchase_token: purchaseToken,
        pay_channel:
          payChannel === PayChannel.APPLE_IAP
            ? PayChannel.APPLE_IAP
            : PayChannel.GOOGLE_IAP,
        ...(backendProductId ? { product_id: backendProductId } : {}),
      },
      {
        headers: mergeAdjustAdidIntoHeaders({}),
      },
    );
    rememberConfirmedToken(purchaseToken);
  } catch (err) {
    Track.log("error", "paymentConfirmHelper", "payment/confirm failed", {
      cause: String(JSON.stringify(err)),
    });
  }
}
